import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { DailyGoalType } from './useDailyGoalSettings';

interface DailyGoalProgress {
  type: DailyGoalType;
  target: number;
  current: number;
  percentage: number;
  remaining: number;
  isComplete: boolean;
}

export function useDailyGoalProgress() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['daily-goal-progress', user?.id],
    queryFn: async (): Promise<DailyGoalProgress> => {
      if (!user) {
        return {
          type: 'time',
          target: 15,
          current: 0,
          percentage: 0,
          remaining: 15,
          isComplete: false,
        };
      }

      // Get user's goal settings
      const { data: prefs } = await supabase
        .from('user_reading_preferences')
        .select('daily_goal_type, daily_goal_target')
        .eq('user_id', user.id)
        .maybeSingle();

      const type = (prefs?.daily_goal_type as DailyGoalType) || 'time';
      const target = prefs?.daily_goal_target || 15;

      const today = new Date().toISOString().split('T')[0];
      let current = 0;

      if (type === 'time') {
        // Calculate from focus sessions
        const { data: focusSessions } = await supabase
          .from('focus_sessions')
          .select('actual_duration_seconds')
          .eq('user_id', user.id)
          .gte('created_at', `${today}T00:00:00`)
          .lte('created_at', `${today}T23:59:59`)
          .eq('was_completed', true);

        if (focusSessions) {
          const totalSeconds = focusSessions.reduce(
            (sum, session) => sum + (session.actual_duration_seconds || 0),
            0
          );
          current = Math.floor(totalSeconds / 60);
        }
      } else if (type === 'pages') {
        // Calculate pages read today from reading_progress_updated AND book_finished activities
        // Get today's progress updates (includes both progress updates and finished books with page data)
        const { data: todayActivities } = await supabase
          .from('activities')
          .select('payload, book_id, created_at, type')
          .eq('user_id', user.id)
          .in('type', ['reading_progress_updated', 'book_finished'])
          .gte('created_at', `${today}T00:00:00`)
          .lte('created_at', `${today}T23:59:59`)
          .order('created_at', { ascending: true });

        if (todayActivities && todayActivities.length > 0) {
          // Get unique book IDs from today's activities
          const bookIds = [...new Set(todayActivities.map(a => a.book_id).filter(Boolean))] as string[];
          
          for (const bookId of bookIds) {
            // Get today's latest activity for this book (could be progress or finished)
            const bookActivities = todayActivities.filter(a => a.book_id === bookId);
            const latestActivity = bookActivities[bookActivities.length - 1];
            const latestPayload = latestActivity?.payload as Record<string, any> | null;
            
            // For book_finished, use total_pages if current_page is not set
            let todayLatestPage = latestPayload?.current_page || 0;
            if (latestActivity?.type === 'book_finished' && !todayLatestPage && latestPayload?.total_pages) {
              todayLatestPage = latestPayload.total_pages;
            }
            
            // Get the page count from before today (last entry from yesterday or earlier)
            const { data: previousActivity } = await supabase
              .from('activities')
              .select('payload')
              .eq('user_id', user.id)
              .eq('book_id', bookId)
              .in('type', ['reading_progress_updated', 'book_finished'])
              .lt('created_at', `${today}T00:00:00`)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            const previousPayload = previousActivity?.payload as Record<string, any> | null;
            const previousPage = previousPayload?.current_page || 0;
            
            // Pages read today = today's latest page - yesterday's last page
            current += Math.max(0, todayLatestPage - previousPage);
          }
        }
      } else if (type === 'chapters') {
        // For chapters, we count books finished today as 1 chapter each
        // (since we don't have explicit chapter tracking)
        // In the future, this could be enhanced with a chapters field
        const { data: finishedToday } = await supabase
          .from('activities')
          .select('id')
          .eq('user_id', user.id)
          .eq('type', 'book_finished')
          .gte('created_at', `${today}T00:00:00`)
          .lte('created_at', `${today}T23:59:59`);

        current = finishedToday?.length || 0;
      }

      const percentage = Math.min((current / target) * 100, 100);
      const remaining = Math.max(target - current, 0);
      const isComplete = current >= target;

      return {
        type,
        target,
        current,
        percentage,
        remaining,
        isComplete,
      };
    },
    enabled: !!user,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}
